/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
// General Utils
import translations from 'utils/translations';
import { useNetwork } from 'services/network';
// Components
import { SdmSpacer } from 'components';
import { Roles, backHomeLink } from 'utils/constants';
import {
  parseProductForDataLayer,
  setECommerceDataLayer,
} from 'utils/dataLayerManager';
import {
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoaderCountContext } from 'context/LoaderV5';
import {
  AllinedPageContainer,
  FaqContainer,
  InsidePageContainer,
  TicketBoughtCard,
  parseDevelonProducts,
} from '../common';

export const WhitePageContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  padding: '30px 0px',
  [`@media (max-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '40px 0px',
  },
}));

const SuccessPage = ({ loggedUser, locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).success;
  const i18nV2 = translations.TicketsV2(intl).SuccessWithActions;
  const i18nBuy = translations.TicketsV2(intl).Buy;
  const isOperator = loggedUser.type === Roles.OPERATOR;
  const { isLoaderActive, setLoaderActive } = useContext(LoaderCountContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const network = useNetwork();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  // States
  const [ticketsInOrder, setTicketsInOrder] = useState();

  // Effects
  useEffect(() => {
    const fetchedOrderId = new URLSearchParams(window.location.search).get(
      'order_id'
    );
    const fetchOrderTickets = async (orId) => {
      showLoader();
      const data = await network.ticketingLambda({
        params: `/tickets?order_id=${orId}`,
        customMethod: 'get',
        data: {},
        federationId: loggedUser?.salId,
      });
      if (!data.error) {
        const initialValue = [];
        const dataItems = data || [];
        let totalAmount = 0;
        for (let index = 0; index < dataItems.length; index++) {
          const element = { ...dataItems[index], quantity: 1 };
          const found = initialValue.findIndex(
            (e) => e.product_id === element.product_id
          );
          if (found > -1) {
            const old = initialValue[found];
            const quantity = element.quantity + old.quantity;
            initialValue.splice(found, 1);
            const update = { ...element, quantity: quantity };
            initialValue.push(update);
          } else {
            initialValue.push(element);
          }
          totalAmount += element.amount;
        }

        const dataLayerItems = [];
        initialValue.map((i, idx) => {
          const product = i.product;
          if (product && product?.id) {
            dataLayerItems.push(
              parseProductForDataLayer({
                product,
                idx,
                isMerch: product.product_type === 'merchandising',
                quantity: i.quantity,
              })
            );
          }
        });
        if (dataLayerItems?.length && data?.[0]?.id && data?.[0]?.amount) {
          setECommerceDataLayer({
            items: dataLayerItems,
            transactionId: fetchedOrderId,
            event: 'purchase',
            totalMount: totalAmount,
          });
        }
        const parsedTickets = parseDevelonProducts({
          tickets: data,
          loggedUser,
          locale,
        });
        setTicketsInOrder(parsedTickets);
      } else {
        setTicketsInOrder([]);
      }
      hideLoader();
    };

    if (fetchedOrderId) fetchOrderTickets(fetchedOrderId);
    else {
      const path = window.location.pathname;
      window.location.href = `${backHomeLink}/${locale}${
        path?.includes('app') ? '/app' : ''
      }/sdm-tickets/my-orders`;
    }
  }, []);

  useEffect(() => {
    if (ticketsInOrder && !ticketsInOrder.length) {
      const path = window.location.pathname;
      window.location.href = `${backHomeLink}/${locale}${
        path?.includes('app') ? '/app' : ''
      }/sdm-tickets/my-orders`;
    }
  }, [ticketsInOrder]);

  const getMyTicketsLink = () => {
    const path = window.location.pathname;
    return `${backHomeLink}/${locale}${
      path?.includes('app') ? '/app' : ''
    }/sdm-tickets/my-orders`;
  };

  const getSustainLink = () => {
    const path = window.location.pathname;
    return `${backHomeLink}/${locale}${path?.includes('app') ? '/app' : ''}${
      locale === 'en' ? '/how-get-there' : '/come-arrivare'
    }`;
  };

  return (
    <div>
      {!isLoaderActive && ticketsInOrder ? (
        <Stack>
          <WhitePageContainer>
            <InsidePageContainer>
              <Typography variant={'xxxl'} component={'h1'}>
                {i18nV2.title}
              </Typography>
              <Typography variant="body1" sx={{ marginTop: '20px' }}>
                {i18nV2.subtitleGoOne} &quot;
                <a
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: theme.palette.primary.main,
                  }}
                  href={getSustainLink()}
                  target="_blank"
                  rel="noreferrer"
                >
                  {i18nV2.subtitleGoLink}
                </a>
                &quot;
                {locale === 'en' ? ` ${i18nV2.subtitleGoPage}` : ''}.
              </Typography>
              <Typography variant="body1" sx={{ marginTop: '20px' }}>
                {i18nV2.subtitleOne}{' '}
                <a
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: theme.palette.primary.main,
                  }}
                  href={getMyTicketsLink()}
                >
                  {i18nV2.subtitleLink}
                </a>
                , {i18nV2.subtitleTwo}
              </Typography>
            </InsidePageContainer>
          </WhitePageContainer>
          <AllinedPageContainer>
            <InsidePageContainer>
              <Typography variant="xl" component={'h2'}>
                {i18nV2.recapTitle}
              </Typography>
              <Stack
                sx={{ mt: '25px' }}
                direction={'column'}
                gap={isMdDown ? '20px' : '25px'}
              >
                {ticketsInOrder?.map((t, idx) => (
                  <TicketBoughtCard key={idx} ticket={t} locale={locale} />
                ))}
              </Stack>
              <FaqContainer
                faqs={{
                  ...i18nBuy,
                  faqList: isOperator
                    ? [i18nBuy.operatorFaqItem, ...i18nBuy.faqList]
                    : i18nBuy.faqList,
                }}
                fullSize={true}
              />
            </InsidePageContainer>
          </AllinedPageContainer>
        </Stack>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SdmSpacer height={isSmDown ? 80 : 150} />
          <Typography variant={'xl-bold'}>{i18n.finalizing}</Typography>
        </div>
      )}
    </div>
  );
};

export default SuccessPage;
